import { useCallback, useEffect, useRef, useState } from "react";

import { createLocalShareCard } from "../features/sharing/localShareCard";
import { colorPalette, type ColorId } from "../design/productConfig";
import { analytics } from "../services/analytics/analytics";
import { errors } from "../services/errors/errors";
import { NetlifyShareReportApi } from "../services/share-api/NetlifyShareReportApi";
import type { CreatedShareReport } from "../services/share-api/ShareReportApi";
import { useTrafficStore } from "../stores/useTrafficStore";
import { Icon } from "./Icons";
import { LocationLabel } from "./LocationLabel";
import { ModalShell } from "./ModalShell";
import { VehicleArt } from "./VehicleArt";

const shareReportApi = new NetlifyShareReportApi();

function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);
  return `${minutes}:${String(remainder).padStart(2, "0")}`;
}

export function TripReport() {
  const report = useTrafficStore((state) => state.report);
  const transportMode = useTrafficStore((state) => state.transportMode);
  const mergeAgain = useTrafficStore((state) => state.mergeAgain);
  const dismissReport = useTrafficStore((state) => state.dismissReport);
  const [message, setMessage] = useState("");
  const [shareSheetOpen, setShareSheetOpen] = useState(false);
  const [createdShare, setCreatedShare] = useState<CreatedShareReport | null>(
    null,
  );
  const [preparedShareImage, setPreparedShareImage] = useState<{
    reportKey: string;
    blob: Blob;
  } | null>(null);
  const shareCreation = useRef<Promise<CreatedShareReport> | null>(null);
  const shareReportKey = useRef<string | null>(null);

  const ensureShareReport =
    useCallback(async (): Promise<CreatedShareReport | null> => {
      if (!report || transportMode !== "supabase") return null;
      if (shareReportKey.current === report.finishedAt && createdShare) {
        return createdShare;
      }
      if (shareReportKey.current !== report.finishedAt) {
        shareReportKey.current = report.finishedAt;
        shareCreation.current = null;
        setCreatedShare(null);
      }
      shareCreation.current ??= shareReportApi.create(report);
      const created = await shareCreation.current;
      setCreatedShare(created);
      return created;
    }, [createdShare, report, transportMode]);

  useEffect(() => {
    if (!report || transportMode !== "supabase") return;
    let active = true;
    void ensureShareReport()
      .then((created) => {
        if (active && created) setCreatedShare(created);
      })
      .catch((error: unknown) => {
        shareCreation.current = null;
        errors.capture(error, { action: "prefetch_share_report" });
      });
    return () => {
      active = false;
    };
  }, [ensureShareReport, report, transportMode]);

  useEffect(() => {
    if (!report) return;
    let active = true;
    void createLocalShareCard(report)
      .then((blob) => {
        if (active) {
          setPreparedShareImage({ reportKey: report.finishedAt, blob });
        }
      })
      .catch((error: unknown) => {
        errors.capture(error, { action: "prefetch_share_image" });
      });
    return () => {
      active = false;
    };
  }, [report]);

  useEffect(() => {
    if (shareSheetOpen) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") dismissReport();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [dismissReport, shareSheetOpen]);

  if (!report) return null;

  const shareImage =
    preparedShareImage?.reportKey === report.finishedAt
      ? preparedShareImage.blob
      : null;
  const shareText = `I joined ${report.peakDriverCount.toLocaleString()} people in The Internet Traffic Jam and added about ${report.addedDelaySeconds.toFixed(1)} seconds of delay. Come make it worse.`;
  const shareUrl = createdShare?.url ?? location.origin;
  const reportColor = report.profile.colorId as ColorId;
  const reportColorHex =
    reportColor in colorPalette
      ? `#${colorPalette[reportColor].toString(16).padStart(6, "0")}`
      : "#ff5a1f";

  const systemShare = async () => {
    const nativeShare = (
      navigator as {
        share?: (data: ShareData) => Promise<void>;
      }
    ).share;
    if (!nativeShare) return;
    const canShare = (
      navigator as {
        canShare?: (data?: ShareData) => boolean;
      }
    ).canShare;
    const linkShare: ShareData = {
      title: "The Internet Traffic Jam",
      text: shareText,
      url: shareUrl,
    };
    let nativePayload = linkShare;
    let method = "native_link";
    if (shareImage && typeof File !== "undefined" && canShare) {
      const file = new File(
        [shareImage],
        `traffic-report-${report.profile.plate}.png`,
        { type: "image/png" },
      );
      const fileShare: ShareData = {
        title: "The Internet Traffic Jam",
        text: `${shareText}\n${shareUrl}`,
        files: [file],
      };
      if (canShare.call(navigator, fileShare)) {
        nativePayload = fileShare;
        method = "native_file";
      }
    }
    analytics.track("share_clicked", { method });
    try {
      await nativeShare.call(navigator, nativePayload);
      setShareSheetOpen(false);
      setMessage("Traffic dispatched.");
      analytics.track("share_completed", { method });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      errors.capture(error, { action: "share_report" });
      setMessage("System sharing stalled. Try Copy Invitation.");
    }
  };

  const copyLink = async () => {
    analytics.track("share_clicked", { method: "copy" });
    try {
      const canonicalShare = await ensureShareReport();
      await navigator.clipboard.writeText(canonicalShare?.url ?? shareUrl);
      setMessage("Link copied.");
      analytics.track("share_completed", { method: "copy" });
    } catch (error) {
      errors.capture(error, { action: "copy_link" });
      setMessage("Copy failed. Use your browser address bar.");
    }
  };

  const copyInvitation = async () => {
    analytics.track("share_clicked", { method: "copy_invitation" });
    try {
      const canonicalShare = await ensureShareReport();
      await navigator.clipboard.writeText(
        `${shareText}\n${canonicalShare?.url ?? shareUrl}`,
      );
      setShareSheetOpen(false);
      setMessage("Invitation copied.");
      analytics.track("share_completed", { method: "copy_invitation" });
    } catch (error) {
      errors.capture(error, { action: "copy_invitation" });
      setMessage("Copy failed. Try Copy Link.");
    }
  };

  const download = async () => {
    analytics.track("share_clicked", { method: "download" });
    try {
      const canonicalShare = await ensureShareReport();
      let blob = shareImage;
      if (canonicalShare) {
        try {
          const response = await fetch(canonicalShare.imageUrls.portrait);
          if (
            response.ok &&
            response.headers.get("content-type") === "image/png"
          ) {
            blob = await response.blob();
          }
        } catch {
          // The pre-rendered local image remains available while offline.
        }
      }
      blob ??= await createLocalShareCard(report);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `traffic-report-${report.profile.plate}.png`;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      setMessage("Traffic report downloaded.");
      analytics.track("share_completed", { method: "download" });
    } catch (error) {
      errors.capture(error, { action: "download_report" });
      setMessage("Image generation failed.");
    }
  };

  return (
    <main className="trip-report" aria-labelledby="trip-report-title">
      <div className="trip-report__card">
        <header>
          <div>
            <span className="eyebrow">DEPARTMENT OF INTERNET CONGESTION</span>
            <h1 id="trip-report-title">Traffic Report</h1>
          </div>
          <div className="trip-report__toolbar">
            <span className="local-preview">
              {createdShare ? "SHARE READY" : "LOCAL PREVIEW"}
            </span>
            <button
              className="icon-button"
              type="button"
              aria-label="Back to the road"
              onClick={() => dismissReport()}
            >
              <Icon name="close" />
            </button>
          </div>
        </header>

        <section className="report-hero">
          <div className="report-vehicle">
            <VehicleArt
              className="report-vehicle-art"
              preset={report.profile.vehiclePreset}
              color={reportColorHex}
            />
            <span>{report.profile.plate}</span>
            <LocationLabel
              location={report.location}
              className="report-vehicle__location"
            />
          </div>
          <div className="report-peak">
            <span>PEAK TRAFFIC</span>
            <strong>{report.peakDriverCount.toLocaleString()}</strong>
            <em>{report.worstState}</em>
          </div>
        </section>

        <dl className="report-metrics">
          <div>
            <dt>Time in traffic</dt>
            <dd>{formatDuration(report.elapsedSeconds)}</dd>
          </div>
          <div>
            <dt>Distance traveled</dt>
            <dd>{report.distanceMiles.toFixed(2)} mi</dd>
          </div>
          <div>
            <dt>Estimated delay endured</dt>
            <dd>{report.enduredDelaySeconds.toFixed(1)} sec</dd>
          </div>
          <div>
            <dt>Estimated delay you added</dt>
            <dd>{report.addedDelaySeconds.toFixed(1)} sec</dd>
          </div>
        </dl>

        <blockquote>
          “You didn’t beat the traffic. You recruited it.”
        </blockquote>

        <div className="report-actions">
          <button
            className="button button--primary"
            type="button"
            onClick={() => {
              analytics.track("share_clicked", { method: "share_sheet" });
              void ensureShareReport()
                .catch((error: unknown) => {
                  errors.capture(error, { action: "prepare_share_sheet" });
                })
                .finally(() => setShareSheetOpen(true));
            }}
          >
            <Icon name="share" />
            Invite More Traffic
          </button>
          <div>
            <button
              className="button button--quiet"
              type="button"
              onClick={() => void copyLink()}
            >
              Copy Link
            </button>
            <button
              className="button button--quiet"
              type="button"
              onClick={() => void download()}
            >
              Download Image
            </button>
            <button
              className="button button--quiet"
              type="button"
              onClick={() => void mergeAgain()}
            >
              Merge Back In
            </button>
          </div>
        </div>
        <p className="report-message" aria-live="polite">
          {message}
        </p>
      </div>
      {shareSheetOpen ? (
        <ModalShell
          eyebrow="SEND MORE TRAFFIC"
          title="Invite people to make it worse"
          className="share-sheet"
          onClose={() => setShareSheetOpen(false)}
        >
          <p className="share-sheet__copy">{shareText}</p>
          <p className="share-sheet__url">{shareUrl}</p>
          <div className="share-sheet__actions">
            {"share" in navigator ? (
              <button
                className="button button--primary"
                type="button"
                onClick={() => void systemShare()}
              >
                Open Share Menu
              </button>
            ) : null}
            <button
              className="button button--quiet"
              type="button"
              onClick={() => void copyInvitation()}
            >
              Copy Invitation
            </button>
            <a
              className="button button--quiet"
              href={`mailto:?subject=${encodeURIComponent("Come make internet traffic worse")}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`}
            >
              Email
            </a>
            <a
              className="button button--quiet"
              href={`https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`}
              target="_blank"
              rel="noreferrer"
            >
              Reddit
            </a>
          </div>
        </ModalShell>
      ) : null}
    </main>
  );
}
